// §3.3 / §7 Provider-Adapter — austauschbar, aufgelöster Provider im Job eingefroren

/** Provenance REQUIRED bei generierten Assets (D39). */
export interface Provenance {
  provider: string;
  modelVersion: string;
  requestId: string;
  seed: number | null;
}

export interface ProviderJobHandle {
  providerId: string;
  providerVersion: number;
  externalId: string;
  raw?: unknown;
}

/** Optionen für Provider-Calls (D27): Abbruch-Signal → die HTTP-fetches respektieren den
 *  Job-Exec-Timeout/Cancel (consumer.ts), statt im Abbruchfall hängen zu bleiben. */
export interface ProviderCallOptions {
  signal?: AbortSignal;
}

export interface ProviderVideoOutput {
  videoUrl: string;
  audioUrl?: string | null;
  provenance: Provenance;
}

/**
 * Provider-Job-Zustand (D27), generisch über den Output-Typ. Async-Provider (video/image)
 * exponieren generate()→Handle + poll(handle)→State, damit ein erschöpftes Poll-Budget den
 * Job NICHT regeneriert, sondern in einem späteren Tick dasselbe Handle weiterpollt (Resume).
 */
export type ProviderJobState<TOutput> =
  | { status: "pending" }
  | { status: "processing"; progress?: number }
  | { status: "done"; output: TOutput }
  | { status: "failed"; error: string };

export type ProviderVideoJobState = ProviderJobState<ProviderVideoOutput>;
export type ProviderImageJobState = ProviderJobState<ProviderImageResult>;
export type ProviderVideoInpaintJobState = ProviderJobState<VideoInpaintResult>;
export type ProviderImageEditJobState = ProviderJobState<ImageEditResult>;

/**
 * D27: das Pro-Tick-Poll-Budget ist erschöpft, der Provider-Job ist aber noch pending/processing
 * (KEIN Fehler). Teil des Provider-Job-Vertrags (nicht providers-intern), damit der Runner ihn
 * vom echten Fehler unterscheiden kann: ProviderJobTimeoutError ⇒ Job pausieren (Resume,
 * Hold NICHT released), das persistierte Handle wird in einem späteren Tick weitergepollt.
 * ⚠️ D134-Zweitnutzung (GPU-Adapter, 524-Cold-Start-Brücke): dort ist das Handle SYNTHETISCH
 * ('modal-gpu') und wird NICHT persistiert/gepollt — der Knoten RE-RUNT beim Resume
 * vollständig. Wer den Runner-PJTE-Pfad ändert, darf kein Handle-Polling voraussetzen.
 */
export class ProviderJobTimeoutError extends Error {
  constructor(readonly handle: ProviderJobHandle) {
    super(`Provider-Job '${handle.externalId}' nicht fertig nach Tick-Budget (Resume läuft weiter).`);
    this.name = "ProviderJobTimeoutError";
  }
}

export interface VideoGenRequest {
  prompt: string;
  startFrameUrl?: string | null;
  /**
   * Endframe → der Adapter nutzt den Pixverse-v6-**Transition**-Endpoint (first_image_url +
   * end_image_url) statt des reinen i2v-Endpoints. D107 (= D76 restauriert): bei loop=on
   * setzt der Build endFrame=startFrame (first==last — der Provider schließt den Loop selbst);
   * bei loop=off setzt ihn die gerichtete Transition (D91, expliziter End-Frame-Input).
   * Fehlt er, bleibt es beim i2v-Endpoint.
   */
  endFrameUrl?: string | null;
  durationS: number;
  fps: number;
  loop: boolean;
  audio: boolean;
  resolution?: string;
}

export interface VideoProvider {
  readonly id: string;
  readonly version: number;
  readonly displayName: string;
  /**
   * `maxPromptChars` (optional, D119-Fix — Mechanik GENERISCH, D125: aktuell setzt es kein
   * Provider): hartes Prompt-Zeichenlimit des Endpoints — der Build reicht es presence-gated
   * an `prompt-enhance` (der Enhancer schreibt von vornherein kompakt). Fehlt es, bleibt der
   * Build byte-identisch (D32).
   */
  readonly limits: {
    maxDurationS: number;
    maxFps: number;
    supportsAudio: boolean;
    resolutions: string[];
    maxPromptChars?: number;
  };
  /** Handle VOR Persist sichern (D27). `opts.signal` bricht den HTTP-Call ab (Job-Timeout/Cancel). */
  generate(req: VideoGenRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle>;
  poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderVideoJobState>;
}

export interface ImageGenRequest {
  prompt: string;
  aspectRatio: string;
  count: number;
  referenceUrls?: string[];
  greenscreen?: boolean;
}

export interface ProviderImageResult {
  images: { url: string }[];
  resolution: string;
  provenance: Provenance;
}

export interface ImageProvider {
  readonly id: string;
  readonly version: number;
  readonly displayName: string;
  readonly limits: { supportedResolutions: string[]; maxRefs: number; supportsGreenscreenDirective: boolean };
  /** deterministisch; im Job eingefroren (D30). */
  resolve(aspectRatio: string): { resolution: string };
  /**
   * D27: generate()→Handle (Submit, VOR Persist sichern) + poll(handle)→State (asynchron).
   * Ein Tick-Budget-Timeout regeneriert NICHT, sondern pollt dasselbe Handle später weiter
   * (Resume) — wie der VideoProvider. Der Runner-Treiber `runImageProviderJob` kapselt das.
   */
  generate(req: ImageGenRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle>;
  poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderImageJobState>;
}

// D50: vision + image-edit (TBD-Provider, swappable) ──────────────────────────

export type VisionDecision = "pass" | "edit" | "reject";
/**
 * D79: empfohlene Chroma-Key-Screen-Farbe — die zum MOTIV KONTRASTIERENDE: viel Grün im Charakter
 * (z.B. grüner Hoodie) → `blue`, viel Blau → `green`. Verhindert, dass der Greenscreen-Key den
 * Charakter mit herauslöst. Steuert sowohl den I2V-Bewegungs-Prompt (Pixverse rendert diesen Screen)
 * als auch den `chroma-key` (entfernt genau diese Farbe).
 */
export type ScreenColor = "green" | "blue";
export interface VisionVerdict {
  verdict: VisionDecision;
  score: Record<string, unknown>;
  /** D79: empfohlene Screen-Farbe (kontrastierend zum Motiv). Fehlt → stromabwärts Default `green`. */
  keyColor?: ScreenColor;
}
export interface VisionRequest {
  imageUrl: string;
  prompt: string;
  action?: string;
  /**
   * D76: Loop-Feasibility verlangt (Spritesheet loop=on). Ist es true, urteilt der VLM ZUSÄTZLICH,
   * ob das Bild einen NAHTLOSEN Loop tragen kann (zyklische/symmetrische Pose; KEINE asymmetrische
   * Verdeckung wie „Hand in der Hosentasche"; Walk-Loop = Mittel-Schritt, NICHT Füße zusammen;
   * taugt als first==last frame). Re-posebar ⇒ 'edit', grundsätzlich untauglich ⇒ 'reject'.
   */
  loop?: boolean;
  /**
   * D114: Re-Validate der Edit-Schleife (input-normalize) — das Bild wurde SOEBEN von der Pipeline
   * editiert, um die Gate-Regeln zu erfüllen. Das Gate urteilt dann konvergenz-orientiert: kosmetische
   * Restmängel (leichte Screen-Unebenheit, Kompressionsartefakte) sind 'pass', kein erneutes 'edit'.
   */
  revalidate?: boolean;
  /**
   * D114: von der Pipeline FIXIERTE Screen-Farbe (D79-ScalarRef — enhance/key/loop lesen dieselbe).
   * Das Gate darf dann KEINE andere Farbe empfehlen (keyColor == screenColor), sonst fordert das
   * Re-Validate eine Farbe, die die Edit-Instruktion nie schreibt → deterministische Endlosschleife.
   */
  screenColor?: ScreenColor;
}
export interface VisionProvider {
  readonly id: string;
  readonly version: number;
  readonly displayName: string;
  /** VLM-Urteil Input-Bild ↔ Prompt/Aktion (D50). `opts.signal` bricht den Call bei Timeout/Cancel
   *  ab (D76: die `input-normalize`-Schleife re-validiert mehrfach → muss abbrechbar sein). */
  judge(req: VisionRequest, opts?: ProviderCallOptions): Promise<VisionVerdict>;
}

export interface ImageEditRequest {
  imageUrl: string;
  instruction: string;
}
export interface ImageEditResult {
  imageUrl: string;
  provenance: Provenance;
}
export interface ImageEditProvider {
  readonly id: string;
  readonly version: number;
  readonly displayName: string;
  /**
   * Bild-Edit nach Instruktion (D50/D76): Normalisierung auf eine loop-taugliche/prompt-treue
   * Pose. D27 (umgestellt vom synchronen `edit` auf das Handle/Resume-Paar, wie Video-/Image-/
   * VideoInpaint-Provider): `generate()` submittet NUR (Handle VOR Persist sichern), `poll()` ist
   * EIN schneller Status-GET. Erschöpft das Pro-Tick-Budget den Poll, pollt ein Resume-Tick
   * dasselbe Handle weiter, statt neu zu submitten — die fal-Queue-Wartezeit ist damit irrelevant
   * (kein Job-Exec-Timeout-Tod mehr, wie zuvor beim VACE-Inpainting). Die D76-Edit-Schleife in
   * `input-normalize` persistiert PRO RUNDE ein eigenes Handle (ctx.providerJob-Ordinal = Runde),
   * damit ein Resume die fertigen Runden weiterpollt statt neu zu submitten. `opts.signal` bricht ab.
   */
  generate(req: ImageEditRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle>;
  poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderImageEditJobState>;
}

// D94: frame-interpolation-Provider (FILM-Naht im Spritesheet-Loop) ───────────

export interface FrameInterpolationRequest {
  /** Erstes Bild der Interpolation (im Loop-Seam: der NEUE End-Frame nach dem Loop-Cut). */
  startImageUrl: string;
  /** Zweites Bild (im Loop-Seam: der Start-Frame frame[0] — die Naht wickelt auf ihn zurück). */
  endImageUrl: string;
  /** Anzahl der ZWISCHEN-Frames (D94: 1–2, nach Naht-Ähnlichkeit). */
  numFrames: number;
}
export interface FrameInterpolationResult {
  /**
   * PNG-Bytes der Zwischenframes in zeitlicher Reihenfolge start→end (OHNE die Eingabebilder).
   * Bewusst Bytes statt URLs: der Adapter lädt die Provider-Outputs selbst (safeFetchBytes,
   * D60-Präzedenz) — der Aufrufer (loop-Capability) reicht sie inline an die GPU-Runtime
   * weiter (frames-append), ohne eigene Fetch-/SSRF-Logik.
   */
  frames: Uint8Array[];
  provenance: Provenance;
}
export type ProviderFrameInterpolationJobState = ProviderJobState<FrameInterpolationResult>;
export interface FrameInterpolationProvider {
  readonly id: string;
  readonly version: number;
  readonly displayName: string;
  /**
   * ML-Frame-Interpolation zwischen zwei Bildern (D94: FILM via fal.ai — glättet die Loop-Naht
   * zwischen neuem End-Frame und Start-Frame). Einzelaufruf mit internem Poll (wie
   * ImageEditProvider.edit — Sekunden-Latenz, bewusst ohne D27-Handle-Resume); `opts.signal`
   * bricht Submit/Poll beim Job-Timeout/Cancel ab.
   */
  interpolate(req: FrameInterpolationRequest, opts?: ProviderCallOptions): Promise<FrameInterpolationResult>;
  /**
   * D167 (OPTIONAL): D27-Handle-Oberfläche (generate→Handle, poll→Status-GET) für Aufrufstellen
   * mit VIELEN sequenziellen Calls (frame-smooth: bis 24 Paare) — per-Call-Handle macht die
   * Schleife tick-sicher (Pause/Resume pollt dasselbe Handle weiter, kein Re-Submit/Re-COGS).
   * FILM (Alt-Spec-Swap) implementiert sie bewusst NICHT (D32: Einzelaufruf-Verhalten bleibt) —
   * Aufrufer prüfen die Präsenz und fallen sonst auf `interpolate` zurück.
   */
  generate?(req: FrameInterpolationRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle>;
  poll?(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderFrameInterpolationJobState>;
}

// D131: video-inpaint-Provider (VACE-Naht-Inpainting im Spritesheet-Loop) ──────

export interface VideoInpaintRequest {
  /** Konditions-Video (Wan-natives 81-Frame-Raster: Kontext-Frames + neutralgraue Lücken). */
  videoUrl: string;
  /** Masken-Video (schwarz = erhalten/Kontext, weiß = generieren) — frame-synchron zu videoUrl. */
  maskVideoUrl: string;
  /** Referenzbild (Identitäts-/Stil-Anker, z. B. frame[0] des Trims). */
  refImageUrl?: string;
  /** Kompakter Motion-Prompt (Leitplanke für das generierte Segment). */
  prompt: string;
  /** Frame-Rate der Kondition (fal: 5–24) — 24 eliminiert den 16-fps-Tempo-Versatz (D131). */
  fps: number;
  /** Ziel-Auflösung des Endpoints (z. B. '480p'); der Aufrufer resized das Ergebnis ohnehin. */
  resolution?: string;
  /**
   * Ziel-Seitenverhältnis des Endpoints ('16:9' | '1:1' | '9:16', D136): die Kondition
   * wird GPU-seitig exakt auf dieses Bucket gepolstert — explizit mitsenden verhindert,
   * dass der Provider per 'auto' ein anderes Bucket wählt und die Geometrie verzerrt.
   */
  aspectRatio?: string;
  /**
   * D154 (Voll-Modus): erzwingt, dass der Output GENAU so viele Frames hat wie die Kondition
   * (fal `match_input_num_frames`). Im Voll-Modus läuft das ganze Loop-Video durch VACE und
   * wird 1:1 ersetzt — ohne dieses Flag defaultet fal auf `num_frames`=81 und streckt/staucht
   * die Bewegung. Im Fenster-Modus (Default) irrelevant (die 21er-Kondition bestimmt die Länge).
   */
  matchInputNumFrames?: boolean;
  seed?: number;
}
export interface VideoInpaintResult {
  /**
   * MP4-Bytes des Ergebnis-Videos. Bewusst Bytes statt URL (FrameInterpolationResult-
   * Präzedenz): der Adapter lädt den Provider-Output selbst (safeFetchBytes, größen-
   * gedeckelt) — der Aufrufer (loop-Capability) reicht sie inline an die GPU-Runtime
   * weiter (frames-append-video), ohne eigene Fetch-/SSRF-Logik.
   */
  videoBytes: Uint8Array;
  provenance: Provenance;
}
export interface VideoInpaintProvider {
  readonly id: string;
  readonly version: number;
  readonly displayName: string;
  /**
   * Maskenbasiertes Video-Inpainting (D131: Wan-VACE via fal — generiert die Loop-Naht-Frames
   * zwischen Trim-Ende und Trim-Anfang). D27 (umgestellt vom synchronen `inpaint` auf das
   * Handle/Resume-Paar, wie Video-/Image-Provider): `generate()` submittet NUR (Handle VOR Persist
   * sichern), `poll()` ist ein EINZELNER, schneller Status-GET (KEIN blockierender SSE-Stream —
   * der Runner-Treiber muss nach dem Tick-Budget pausieren können). Erschöpft das Pro-Tick-Budget
   * den Poll ohne terminalen Status, pollt ein späterer Resume-Tick DASSELBE Handle weiter, statt
   * neu zu submitten — die fal-Queue-Wartezeit ist damit irrelevant (kein Job-Exec-Timeout/keine
   * Deploy-Eviction/keine doppelten fal-COGS). `opts.signal` bricht Submit/Poll/Download ab.
   */
  generate(req: VideoInpaintRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle>;
  poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderVideoInpaintJobState>;
}

// D146: bg-remove-Provider (AI-Matting via fal — KEIN Greenscreen nötig, Alternative zum chroma-key) ──
//   • VideoBgRemoveProvider (BEN2 `fal-ai/ben/v2/video`): Video → transparentes WebM (VP9-Alpha).
//   • ImageBgRemoveProvider (Bria RMBG 2.0 `fal-ai/bria/background/remove`): Bild → transparentes PNG.
// Beide D27 (generate/poll), gleicher FAL_API_KEY wie die übrigen fal-Adapter, host-pinned (SSRF).

export interface VideoBgRemoveRequest {
  /** Quell-Video (presigned-GET/öffentliche URL), dessen Hintergrund entfernt werden soll. */
  videoUrl: string;
  /**
   * Optionale Hintergrund-Ersatzfarbe (RGB 0–255). Fehlt → echte Transparenz (WebM-Alpha,
   * `output_format=webm`). Loumy lässt sie weg ⇒ transparentes WebM, das die GPU-Runtime
   * alpha-erhaltend zu RGBA-Frames dekodiert.
   */
  backgroundColor?: [number, number, number];
  seed?: number;
}
export interface VideoBgRemoveResult {
  /**
   * WebM-Bytes MIT Alpha (VP9 `yuva420p`). Bewusst Bytes statt URL (VideoInpaintResult-Präzedenz):
   * der Adapter lädt den fal-Output selbst (safeFetchBytes, größen-gedeckelt) — der Aufrufer
   * (video-bg-remove-Capability) reicht sie inline an die GPU-Runtime weiter (bg-decode-alpha),
   * ohne eigene Fetch-/SSRF-Logik.
   */
  videoBytes: Uint8Array;
  provenance: Provenance;
}
export type ProviderVideoBgRemoveJobState = ProviderJobState<VideoBgRemoveResult>;
export interface VideoBgRemoveProvider {
  readonly id: string;
  readonly version: number;
  readonly displayName: string;
  /**
   * Video-Hintergrund-Entfernung via AI-Matting (D146, BEN2). D27 (generate/poll wie die übrigen
   * fal-Provider): `generate()` submittet NUR (Handle VOR Persist sichern), `poll()` ist EIN
   * schneller Status-GET; ein erschöpftes Tick-Budget pollt dasselbe Handle im Resume-Tick weiter
   * (kein Re-Submit) ⇒ die fal-Queue-Wartezeit ist tick-übergreifend. `opts.signal` bricht ab.
   */
  generate(req: VideoBgRemoveRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle>;
  poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderVideoBgRemoveJobState>;
}

export interface ImageBgRemoveRequest {
  /** Quell-Bild (presigned-GET/öffentliche URL), dessen Hintergrund entfernt werden soll. */
  imageUrl: string;
}
export interface ImageBgRemoveResult {
  /** URL des transparenten PNG (fal-CDN). Die Capability lädt/persistiert es wie ein gen-Bild. */
  imageUrl: string;
  provenance: Provenance;
}
export type ProviderImageBgRemoveJobState = ProviderJobState<ImageBgRemoveResult>;
export interface ImageBgRemoveProvider {
  readonly id: string;
  readonly version: number;
  readonly displayName: string;
  /**
   * Bild-Hintergrund-Entfernung via AI-Matting (D146, Bria RMBG 2.0). D27 (generate/poll):
   * `generate()` submittet, `poll()` ist ein Status-GET, COMPLETED → transparentes PNG laden.
   * Resume-fähig (Handle weiterpollen). `opts.signal` bricht ab.
   */
  generate(req: ImageBgRemoveRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle>;
  poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderImageBgRemoveJobState>;
}

// D163: image-upscale-Provider (Super-Resolution via fal — Bild rein, hochskaliertes Bild raus) ──
//   • ImageUpscaleProvider (Real-ESRGAN `fal-ai/esrgan`): treue Super-Resolution, kein Prompt.
// D27 (generate/poll), gleicher FAL_API_KEY wie die übrigen fal-Adapter, host-pinned (SSRF).

/** D163: Modell-Variante (abstrakt, provider-mappt auf das konkrete Real-ESRGAN-Netz). */
export type ImageUpscaleVariant = "general" | "anime";
export interface ImageUpscaleRequest {
  /** Quell-Bild (presigned-GET/öffentliche URL), das hochskaliert werden soll. */
  imageUrl: string;
  /** Ziel-Skalierungsfaktor (2 oder 4 — der Adapter reicht ihn als ESRGAN-`scale` durch). */
  scale: number;
  /** Abstrakte Variante: `general` (Foto/3D) oder `anime` (saubere Kanten für Sprites/Pixel-Art). */
  variant: ImageUpscaleVariant;
}
export interface ImageUpscaleResult {
  /** URL des hochskalierten Bildes (fal-CDN). Die Capability lädt/persistiert es wie ein gen-Bild. */
  imageUrl: string;
  provenance: Provenance;
}
export type ProviderImageUpscaleJobState = ProviderJobState<ImageUpscaleResult>;
export interface ImageUpscaleProvider {
  readonly id: string;
  readonly version: number;
  readonly displayName: string;
  /**
   * Bild-Super-Resolution (D163, Real-ESRGAN). D27 (generate/poll): `generate()` submittet,
   * `poll()` ist ein Status-GET, COMPLETED → hochskaliertes Bild (URL) laden. Resume-fähig
   * (Handle weiterpollen). `opts.signal` bricht ab.
   */
  generate(req: ImageUpscaleRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle>;
  poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderImageUpscaleJobState>;
}

// D168: video-enhance-Provider (Video-Super-Resolution/Detail-Recovery via fal — Video rein,
// schärferes/hochskaliertes Video raus) ──
//   • VideoEnhanceProvider (SeedVR2 `fal-ai/seedvr/upscale/video`): temporal-konsistente
//     Detail-Rekonstruktion — gewinnt lt. 2026-Vergleichen gerade bei kurzen, KI-generierten,
//     komprimierten Clips (genau unsere Spritesheet-Loops). D27 (generate/poll), gleicher
//     FAL_API_KEY, host-pinned (SSRF).

export interface VideoEnhanceRequest {
  /** Quell-Video (presigned-GET/öffentliche URL), das verbessert/hochskaliert werden soll. */
  videoUrl: string;
  /** Skalierungsfaktor (Default 2 — der Adapter reicht ihn als `upscale_factor` durch). */
  upscaleFactor?: number;
}
export interface VideoEnhanceResult {
  /** URL des verbesserten Videos (fal-CDN). Die Capability persistiert es wie ein gen-Video. */
  videoUrl: string;
  provenance: Provenance;
}
export type ProviderVideoEnhanceJobState = ProviderJobState<VideoEnhanceResult>;
export interface VideoEnhanceProvider {
  readonly id: string;
  readonly version: number;
  readonly displayName: string;
  /**
   * Video-Super-Resolution (D168, SeedVR2). D27 (generate/poll): `generate()` submittet,
   * `poll()` ist ein Status-GET, COMPLETED → verbessertes Video (URL). Resume-fähig
   * (Handle weiterpollen). `opts.signal` bricht ab.
   */
  generate(req: VideoEnhanceRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle>;
  poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderVideoEnhanceJobState>;
}

// D60: llm-Provider (Prompt-Veredelung, swappable) ─────────────────────────────

export interface LLMRequest {
  /** Roh-Prompt, der veredelt werden soll. */
  prompt: string;
  /** Optionale System-Instruktion (Rolle/Stil-Leitplanken). */
  system?: string;
  /** Obergrenze der Antwort-Tokens (Kosten-/Längenkontrolle). */
  maxTokens?: number;
  /**
   * D80: optionale Referenzbild-URLs (multimodal). prompt-enhance reicht hier das vom User
   * HOCHGELADENE Bild durch (image-generator-Edit-Modus, §10.2) — damit der LLM Stil/Look des
   * Bildes SIEHT und bewahrt, statt ihn blind zu erraten (kein „hyperrealistic"-Drift gegen ein
   * stilisiertes Upload). Multimodal-fähige Provider (Gemini) hängen sie als inline_data an; rein
   * textuelle Provider ignorieren das Feld (graziös). URLs sind presigned-GET/Quell-URLs (D56).
   */
  imageUrls?: string[];
}
export interface LLMResult {
  /** Veredelter Text; leer ⇒ der Aufrufer fällt auf den Roh-Prompt zurück. */
  text: string;
}
export interface LLMProvider {
  readonly id: string;
  readonly version: number;
  readonly displayName: string;
  /**
   * Prompt-Veredelung für prompt-enhance (D60). Primär Text→Text; bei `req.imageUrls` (D80)
   * multimodal (Bild→Text), sofern der Provider es unterstützt. Kein Asset ⇒ keine Provenance (D39).
   */
  complete(req: LLMRequest): Promise<LLMResult>;
}

export interface ProviderRegistry {
  register(slot: string, p: object): void;
  resolve<T>(slot: string, name?: string): T;
}

/** Adapter ohne konfigurierten Key/Endpoint werfen dies definiert (echte Skeletons, §13). */
export class ProviderUnavailableError extends Error {
  constructor(
    readonly slot: string,
    readonly providerId: string,
    message?: string,
  ) {
    super(message ?? `Provider '${providerId}' (slot '${slot}') ist nicht konfiguriert (fehlender Key/Endpoint).`);
    this.name = "ProviderUnavailableError";
  }
}
